import { GNEventLog } from "../analyticsUser/types";

// sankey data interface
export interface SankeyDataLink {
  source:string,
  target:string,
  value:number
}
  
export interface SankeyDataNode {
  name: string,
  value: number,
  // depth: number
  layer: number
}
  
export interface SankeyDataInterface {
  links:SankeyDataLink[], 
  nodes:SankeyDataNode[],
  highestValue: number;
  totalValue: number;
}
// BE data interfaces
export interface GNAnalyticsLocationFlowItem {
  from: string,
  to: string,
  weight: number,
  layer: number
}

export interface GNAICrashOccurrence {
    projectID: string;
    crashGroupID: string;
    clientType: 'client' | 'server' | 'editor' | string;
    eventTimestamp: number;
    eventLocation: string;
    userID: string;
    userName: string;
    pluginVersion: string;
    projectVersion: string;
    platformOS: string;
    platformCPU: string;
    platformGPU: string;
    platformRAM: string;
    downloadURL?: string;
}

export interface GNAICrashGroupItem {
    crashGroupID: string;
    aiSummary: string;
    userSummary: string;
    occurrenceCount: number;
    firstSeenTimestamp: number;
    lastSeenTimestamp: number;
    exampleEventLog?: GNEventLog;
    recentOccurrences: GNAICrashOccurrence[];
}

export interface GNAnalyticsProjectClientDailyStatsItem {
  day: number,
  busySessionHours: number[],
  uniqueUsers: number,
  sessions: number,
  sessionAvgDuration: number,
  crashes: number,
  ensures: number
}

export interface GNAnalyticsProjectClientTypeItem {
  latestUniqueUsers: {
    userID: string,
    userName: string,
    country: string,
    latestSessionTimestamp: number,
    isOnline?: boolean,
    isInEditor?: boolean
  }[],
  geo: Record<string, number>, // country: amount of unique users
  platformOS: Record<string, number>, // platform: amount of unique users
  platformCPU: Record<string, number>, // platform: amount of unique users
  platformGPU: Record<string, number>, // platform: amount of unique users
  platformRAM: Record<string, number>, // platform: amount of unique users
  locations: Record<string, number>, // location: amount of visits
  locationFlows: GNAnalyticsLocationFlowItem[],
  locationFlowsSankeyData: SankeyDataInterface,
  dailyStats: GNAnalyticsProjectClientDailyStatsItem[],
  crashGroups:GNAICrashGroupItem[]
}

export interface GNAnalyticsReportDownload {
  reportAction: string,
  reportTimestamp: number,
  reportUserID: string,
  reportUserName: string,
  reportClientType: 'client' | 'server' | 'editor' | string,
  reportLocation: string,
  reportProjectVersion: string,
  reportDownloadURL: string,
  reportIsUnderGNAIProcessing: boolean
}

export interface GNAnalyticsProjectData {
  projectID: string,
  editor: GNAnalyticsProjectClientTypeItem,
  server: GNAnalyticsProjectClientTypeItem,
  client: GNAnalyticsProjectClientTypeItem,
  reportDownloads: GNAnalyticsReportDownload[]
  crashGroupsOccurrenceSinceTimestamp: number;
  crashGroupsProcessiongReportsCount: number;
}

/**
 * Defines Analyticd Project State interface
 */
export interface AnalyticsProjectState {
    // http state
    httpInProgress: boolean;
    httpError: string;
    // crash group http state
    httpCrashGroupInProgress: boolean;
    httpCrashGroupError: string;
    // crash group update http state
    httpCrashGroupUpdateInProgress: boolean;
    httpCrashGroupUpdateError: string;
    // BE data
    projectData: GNAnalyticsProjectData;
  }
  
  /**
   * Defines Analytics Project actions names
   */
  export const ANALYTICS_PROJECT_IN_PROGRESS = 'ANALYTICS_PROJECT_IN_PROGRESS';
  export const ANALYTICS_PROJECT_ERROR = 'ANALYTICS_PROJECT_ERROR';
  export const ANALYTICS_PROJECT_GET = 'ANALYTICS_PROJECT_GET';
  export const ANALYTICS_CRASH_GROUP_IN_PROGRESS = 'ANALYTICS_CRASH_GROUP_GET_IN_PROGRESS';
  export const ANALYTICS_CRASH_GROUP_ERROR = 'ANALYTICS_CRASH_GROUP_GET_ERROR';
  export const ANALYTICS_CRASH_GROUP_GET = 'ANALYTICS_CRASH_GROUP_GET';
  export const ANALYTICS_CRASH_GROUP_UPDATE_IN_PROGRESS = 'ANALYTICS_CRASH_GROUP_UPDATE_IN_PROGRESS';
  export const ANALYTICS_CRASH_GROUP_UPDATE_ERROR = 'ANALYTICS_CRASH_GROUP_UPDATE_ERROR';
  export const ANALYTICS_CRASH_GROUP_UPDATE = 'ANALYTICS_CRASH_GROUP_UPDATE';
  
  /**
   * Defines Analytics Project actions interfaces
   */
  interface AnalyticsProjectInProgressAction {
    type: typeof ANALYTICS_PROJECT_IN_PROGRESS;
  }
  interface AnalyticsProjectErrorAction {
    type: typeof ANALYTICS_PROJECT_ERROR;
    error: string;
  }
  interface AnalyticsProjectGetAction {
    type: typeof ANALYTICS_PROJECT_GET;
    projectData: GNAnalyticsProjectData;
  }
  interface AnalyticsCrashGroupInProgressAction {
    type: typeof ANALYTICS_CRASH_GROUP_IN_PROGRESS;
  }
  interface AnalyticsCrashGroupErrorAction {
    type: typeof ANALYTICS_CRASH_GROUP_ERROR;
    error: string;
  }
  interface AnalyticsCrashGroupGetAction {
    type: typeof ANALYTICS_CRASH_GROUP_GET;
    projectData: GNAnalyticsProjectData;
  }
  interface AnalyticsCrashGroupUpdateInProgressAction {
    type: typeof ANALYTICS_CRASH_GROUP_UPDATE_IN_PROGRESS;
  }
  interface AnalyticsCrashGroupUpdateErrorAction {
    type: typeof ANALYTICS_CRASH_GROUP_UPDATE_ERROR;
    error: string;
  }
  interface AnalyticsCrashGroupUpdateAction {
    type: typeof ANALYTICS_CRASH_GROUP_UPDATE;
    projectData: GNAnalyticsProjectData;
  }
  
  /**
   * Defines all suported actions for Analytics Project
   */
  export type AnalyticsProjectActionTypes =
    AnalyticsProjectInProgressAction |
    AnalyticsProjectErrorAction |
    AnalyticsProjectGetAction |
    AnalyticsCrashGroupInProgressAction |
    AnalyticsCrashGroupErrorAction |
    AnalyticsCrashGroupGetAction |
    AnalyticsCrashGroupUpdateInProgressAction |
    AnalyticsCrashGroupUpdateErrorAction |
    AnalyticsCrashGroupUpdateAction;
  
  /**
   * Defines AnalyticsProjectActionsInterface
   */
  export interface AnalyticsProjectActionsInterface {
    analyticsProjectGet: (projectID: string) => Promise<void>;
    analyticsCrashGroupGet: ( projectID: string, crashGroupID: string ) => Promise<void>;
    analyticsCrashGroupUpdateSummary: ( projectID: string, crashGroupID: string, userSummary: string ) => Promise<void>;
  }
  